import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Layers,
  TrendingUp,
  Building2,
  HardHat,
  User,
  FileText,
  Wallet,
  Target,
  BarChart3,
  PieChart,
  RefreshCw,
} from 'lucide-react';
import { GlassCard, GlassButton, GlassChart, FadeIn } from '../../components/ui';
import { api } from '../../hooks/useApi';

const BOOK_META = {
  imobiliaria: { name: 'Imobiliaria', color: '#22c55e', icon: Building2 },
  construtora: { name: 'Construtora', color: '#3b82f6', icon: HardHat },
  pessoal: { name: 'Pessoal', color: '#8b5cf6', icon: User },
  geral: { name: 'Geral', color: '#94a3b8', icon: FileText },
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatPercent = (value) => `${Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

export const FinanceiroConsolidado = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadConsolidado = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/contabilidade/consolidado');
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar consolidado.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConsolidado();
  }, [loadConsolidado]);

  const metrics = data?.metrics || {};
  const abertas = metrics.abertas || {};
  const books = data?.books || [];
  const monthly = data?.monthly || [];
  const assets = data?.assets || [];

  const bookTotalReceita = useMemo(
    () => books.reduce((acc, item) => acc + Number(item.receita || 0), 0),
    [books]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Visao Consolidada</h1>
          <p className="text-white/50">
	            {loading ? 'Carregando consolidado real...' : `Realizado no periodo ${data?.periodo?.inicio || '-'} a ${data?.periodo?.fim || '-'} + aberto total`}
          </p>
        </div>
        <GlassButton variant="secondary" icon={RefreshCw} onClick={loadConsolidado} loading={loading}>
          Atualizar
        </GlassButton>
      </div>

      {error && (
        <GlassCard className="p-4 border-red-500/30 bg-red-500/10" hover={false}>
          <p className="text-sm text-red-300">{error}</p>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
	              <p className="text-sm text-white/50">Patrimonio liquido realizado</p>
              <p className="text-2xl font-bold text-white/90">{formatCurrency(metrics.patrimonio_liquido)}</p>
            </div>
            <div className="p-3 rounded-xl bg-accent-primary/20">
              <Wallet className="w-6 h-6 text-accent-primary" />
            </div>
          </div>
	          <div className="mt-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-green-400" />
	            <span className="text-sm text-white/40">saldo bancario + resultado pago</span>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Margem liquida</p>
              <p className={`text-2xl font-bold ${Number(metrics.margem_liquida || 0) >= 0 ? 'text-accent-primary' : 'text-red-400'}`}>
                {formatPercent(metrics.margem_liquida)}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-accent-primary/20">
              <Target className="w-6 h-6 text-accent-primary" />
            </div>
          </div>
          <div className="mt-3 text-sm text-white/40">resultado / receita paga</div>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
	              <p className="text-sm text-white/50">Previsao saldo aberto</p>
              <p className={`text-2xl font-bold ${Number(metrics.previsao_saldo || 0) >= 0 ? 'text-yellow-400' : 'text-red-400'}`}>
                {formatCurrency(metrics.previsao_saldo)}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-yellow-500/20">
              <BarChart3 className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
          <div className="mt-3 text-sm text-white/40">
            {formatCurrency(metrics.a_receber)} a receber / {formatCurrency(metrics.a_pagar)} a pagar
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
	              <p className="text-sm text-white/50">Resultado realizado</p>
              <p className={`text-2xl font-bold ${Number(metrics.resultado || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatCurrency(metrics.resultado)}
              </p>
	      </div>

	      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
	        <GlassCard className="p-5 border-red-500/20 bg-red-500/5">
	          <p className="text-sm text-white/50">A receber vencido</p>
	          <p className="text-xl font-bold text-red-300">{formatCurrency(abertas.receitas_vencidas)}</p>
	        </GlassCard>
	        <GlassCard className="p-5 border-blue-500/20 bg-blue-500/5">
	          <p className="text-sm text-white/50">A receber proximos 90 dias</p>
	          <p className="text-xl font-bold text-blue-300">{formatCurrency(abertas.receitas_a_vencer_periodo)}</p>
	        </GlassCard>
	        <GlassCard className="p-5 border-white/10 bg-white/[0.02]">
	          <p className="text-sm text-white/50">A receber futuro</p>
	          <p className="text-xl font-bold text-white/80">{formatCurrency(abertas.receitas_futuras)}</p>
	        </GlassCard>
	        <GlassCard className="p-5 border-orange-500/20 bg-orange-500/5">
	          <p className="text-sm text-white/50">A pagar aberto</p>
	          <p className="text-xl font-bold text-orange-300">{formatCurrency(metrics.a_pagar)}</p>
	        </GlassCard>
	      </div>
            <div className="p-3 rounded-xl bg-green-500/20">
              <PieChart className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <div className="mt-3 text-sm text-white/40">{metrics.movimentacoes || 0} movimentacoes no periodo</div>
        </GlassCard>
      </div>

      <FadeIn>
        <GlassCard>
          <div className="p-6 border-b border-white/10">
            <h3 className="text-lg font-semibold text-white/90">Distribuicao por Book</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {books.map((book) => {
                const meta = BOOK_META[book.key] || BOOK_META.geral;
                const Icon = meta.icon;
                const participation = bookTotalReceita > 0 ? (Number(book.receita || 0) / bookTotalReceita) * 100 : 0;
                return (
                  <div
                    key={book.key}
                    className="p-6 rounded-xl border border-white/10"
                    style={{ backgroundColor: `${meta.color}10` }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: `${meta.color}30` }}>
                        <Icon className="w-5 h-5" style={{ color: meta.color }} />
                      </div>
                      <span className="font-semibold text-white/90">{meta.name}</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
	                        <span className="text-white/50">Receita paga</span>
                        <span style={{ color: meta.color }}>{formatCurrency(book.receita)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
	                        <span className="text-white/50">Resultado realizado</span>
                        <span className={Number(book.resultado || 0) >= 0 ? 'text-green-400' : 'text-red-400'}>
                          {formatCurrency(book.resultado)}
                        </span>
                      </div>
	                      <div className="flex justify-between text-sm">
	                        <span className="text-white/50">Aberto</span>
	                        <span className="text-yellow-300">{formatCurrency(book.pendente)}</span>
	                      </div>
	                      <div className="flex justify-between text-sm">
	                        <span className="text-white/50">Participacao</span>
                        <span style={{ color: meta.color }}>{formatPercent(participation)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </GlassCard>
      </FadeIn>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FadeIn delay={0.1}>
          <GlassCard>
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white/90">Evolucao Consolidada</h3>
            </div>
            <div className="p-6">
              <GlassChart
                type="area"
                data={monthly}
                dataKeys={[
                  { key: 'imobiliaria', name: 'Imobiliaria' },
                  { key: 'construtora', name: 'Construtora' },
                  { key: 'pessoal', name: 'Pessoal' },
                  { key: 'geral', name: 'Geral' },
                ]}
                xAxisKey="month"
                height={280}
                colors={['#22c55e', '#3b82f6', '#8b5cf6', '#94a3b8']}
                stacked
              />
            </div>
          </GlassCard>
        </FadeIn>

        <FadeIn delay={0.2}>
          <GlassCard>
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white/90">Saldos por Conta</h3>
            </div>
            <div className="p-6">
              {assets.length > 0 ? (
                <GlassChart
                  type="pie"
                  data={assets}
                  dataKeys={['value']}
                  height={280}
                />
              ) : (
                <div className="h-[280px] flex items-center justify-center text-white/50">
                  <div className="text-center">
                    <Layers className="w-8 h-8 mx-auto mb-3 text-white/30" />
                    <p>Nenhuma conta bancaria cadastrada.</p>
                  </div>
                </div>
              )}
            </div>
          </GlassCard>
        </FadeIn>
      </div>
    </div>
  );
};

export default FinanceiroConsolidado;
